import { faChevronDown, faChevronRight, faClone, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { BUILD_READS_REMOTE_CONFIG, CURRENT_GAME_BUILD, NOT_READ_BY_BUILD } from '~/team_tools/components/buildReads'
import { DateTimeField } from '~/team_tools/components/DateTimeField'
import { showInZone, toOffsetIso, useZone, utcToZoned, zonedToUtc } from '~/team_tools/components/timezone'

/**
 * When the game is open, as a line rather than as JSON.
 *
 * This is the key that decides whether players can get in at all, and it was
 * a textarea of hand-typed ISO strings with offsets in them. One mistyped
 * "-04:00" and the playtest opens at the wrong hour in every timezone, and
 * nothing on the screen says so until somebody cannot log in.
 *
 * So: two pickers, read as a sentence, in whichever zone is chosen at the top
 * of the page. The ISO is written out with that zone's own offset at that
 * instant -- which also means a time in August and one in December each get
 * the right one without anybody thinking about daylight saving.
 *
 * Nothing here says "preseason". The key is called that in the build; what it
 * means on this screen is when the game goes live.
 *
 * The list is the whole of the permission: the game is open during these
 * times and shut the rest of the time. So an empty list is nobody getting in,
 * not "no restriction", and the screen says which in words.
 */
export type GameWindow = { betaStartTime: string; betaEndTime: string }

export type GameWindowValue = {
    header: string
    subDescription: string
    footer: string
    eventsList: GameWindow[]
}

export const EMPTY_WINDOWS: GameWindowValue = {
    header: 'WELCOME TO THE BR1 PLAYTEST',
    subDescription: 'NEXT AVAILABLE PLAYTEST:',
    footer: 'THE SERVERS WILL REMAIN CLOSED UNTIL THE START OF THE NEXT PLAYTEST.',
    eventsList: [],
}

const INPUT =
    'w-full rounded border border-white/20 bg-transparent px-2 py-1 text-xs outline-none focus:border-white/40'

/** The value as it stands, whatever shape it arrived in. A key nobody has set
 *  yet, or one holding something unexpected, still has to open. */
export const asWindows = (value: unknown): GameWindowValue => {
    const v = (value ?? {}) as Partial<GameWindowValue>
    return {
        header: typeof v.header === 'string' ? v.header : EMPTY_WINDOWS.header,
        subDescription:
            typeof v.subDescription === 'string' ? v.subDescription : EMPTY_WINDOWS.subDescription,
        footer: typeof v.footer === 'string' ? v.footer : EMPTY_WINDOWS.footer,
        eventsList: Array.isArray(v.eventsList) ? v.eventsList.filter((e) => e && typeof e === 'object') : [],
    }
}

const isPast = (iso: string) => {
    const t = new Date(iso).getTime()
    return Number.isFinite(t) && t < Date.now()
}

export const GameWindowEditor = ({
    value,
    onChange,
    /** Whether the game is actually taking its hours from this key. False
     *  while nobody has set it, or while it is switched off: the client then
     *  uses whatever is compiled into the build, which this screen cannot
     *  see and must not pretend to know. */
    inForce = true,
}: {
    value: GameWindowValue
    onChange: (next: GameWindowValue) => void
    inForce?: boolean
}) => {
    const [zone] = useZone()
    // The three lines players read on the closed screen. Real, but not what
    // anybody opens this for, so they start out of the way.
    const [showText, setShowText] = useState(false)
    // Times that have already run stay on the value -- removing one is a
    // choice, not housekeeping this screen does -- but they are not what
    // anybody is here to read.
    const [showPast, setShowPast] = useState(false)

    const set = (patch: Partial<GameWindowValue>) => onChange({ ...value, ...patch })

    const setWindow = (index: number, patch: Partial<GameWindow>) =>
        set({ eventsList: value.eventsList.map((w, i) => (i === index ? { ...w, ...patch } : w)) })

    const addWindow = () => {
        // Starts on the next whole hour and runs two, which is the shape of
        // nearly every playtest and is quicker to adjust than to compose.
        const start = new Date()
        start.setMinutes(0, 0, 0)
        start.setHours(start.getHours() + 1)
        const end = new Date(start.getTime() + 2 * 3600_000)
        set({
            eventsList: [
                ...value.eventsList,
                {
                    betaStartTime: toOffsetIso(start.toISOString(), zone) ?? start.toISOString(),
                    betaEndTime: toOffsetIso(end.toISOString(), zone) ?? end.toISOString(),
                },
            ],
        })
    }

    const removeWindow = (index: number) => set({ eventsList: value.eventsList.filter((_, i) => i !== index) })

    /** The same instant one calendar day later, read as wall-clock time in the
     *  chosen zone. Not +24 hours: across a clock change that lands an hour
     *  off, and "10:00 tomorrow" is what was meant. */
    const nextDay = (iso: string) => {
        const { date, time } = utcToZoned(iso, zone)
        if (!date) return iso
        const [y, m, d] = date.split('-').map(Number)
        const next = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10)
        const utc = zonedToUtc(next, time || '00:00', zone)
        return (utc && toOffsetIso(utc, zone)) ?? iso
    }

    /** Copies a time to the following day, so a daily playtest is one click
     *  per day rather than four pickers. */
    const duplicateWindow = (index: number) => {
        const w = value.eventsList[index]
        set({
            eventsList: [
                ...value.eventsList,
                { betaStartTime: nextDay(w.betaStartTime), betaEndTime: nextDay(w.betaEndTime) },
            ],
        })
    }

    /** Every time that has already run, dropped in one go.
     *
     *  A scheduled job does this hourly, so this is the impatient version --
     *  and the only version for the case the job deliberately leaves alone,
     *  which is when every window has expired and removing them would empty
     *  the list. */
    const clearPast = () => set({ eventsList: value.eventsList.filter((w) => !isPast(w.betaEndTime)) })

    // Sorted for reading, but edited by the position the value actually has:
    // sorting the underlying list as somebody types a date would move the row
    // out from under the cursor.
    const rows = value.eventsList
        .map((w, index) => ({ w, index }))
        .sort((a, b) => new Date(a.w.betaStartTime).getTime() - new Date(b.w.betaStartTime).getTime())

    const now = Date.now()
    const live = rows.filter(({ w }) => new Date(w.betaStartTime).getTime() <= now && !isPast(w.betaEndTime))
    const later = rows.filter(({ w }) => new Date(w.betaStartTime).getTime() > now)
    const past = rows.filter(({ w }) => isPast(w.betaEndTime))

    const row = ({ w, index }: { w: GameWindow; index: number }) => {
        const start = new Date(w.betaStartTime).getTime()
        const end = new Date(w.betaEndTime).getTime()
        const backwards = Number.isFinite(start) && Number.isFinite(end) && start >= end
        const over = isPast(w.betaEndTime)
        return (
            <div
                key={index}
                className={`flex flex-col gap-2 rounded border px-3 py-2 ${
                    backwards ? 'border-red-400/50' : over ? 'border-white/5' : 'border-white/15'
                }`}
            >
                <div className='flex flex-row flex-wrap items-center gap-2'>
                    <span className={over ? 'text-white/30' : 'text-white/70'}>Game goes live at</span>
                    <DateTimeField
                        value={w.betaStartTime}
                        onChange={(v) => setWindow(index, { betaStartTime: toOffsetIso(v, zone) ?? '' })}
                    />
                    <span className={over ? 'text-white/30' : 'text-white/70'}>and ends at</span>
                    <DateTimeField
                        value={w.betaEndTime}
                        onChange={(v) => setWindow(index, { betaEndTime: toOffsetIso(v, zone) ?? '' })}
                    />
                    <button
                        type='button'
                        onClick={() => duplicateWindow(index)}
                        title='Add the same times on the following day'
                        className='ml-auto text-[10px] uppercase tracking-widest text-amber-400 hover:text-amber-300'
                    >
                        <FontAwesomeIcon icon={faClone} /> duplicate to next day
                    </button>
                    <button
                        type='button'
                        onClick={() => removeWindow(index)}
                        className='text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300'
                    >
                        <FontAwesomeIcon icon={faTrash} /> remove
                    </button>
                </div>
                <span className='text-[10px] text-white/30'>
                    {backwards
                        ? 'It ends before it starts.'
                        : over
                          ? `Over — ran ${showInZone(w.betaStartTime, zone)}`
                          : `${showInZone(w.betaStartTime, zone)} → ${showInZone(w.betaEndTime, zone)}`}
                </span>
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-3'>
            {/* ONE line about the state, not three stacked banners.
              *
              * There were up to three at once -- the build not reading this
              * key, the list being empty, every time having passed -- each in
              * its own coloured box, saying overlapping things. A screen whose
              * job is "when is the game open" opened with a wall of warnings
              * above the answer.
              *
              * The times below are the whole of the permission: the game is
              * open during them and shut the rest of the time, so an empty
              * list is nobody getting in rather than no restriction. That is
              * worth one red line, not a paragraph. */}
            {!BUILD_READS_REMOTE_CONFIG ? (
                <div
                    className='rounded border border-amber-300/40 px-3 py-2 text-amber-300'
                    title={`${NOT_READ_BY_BUILD}; it takes its hours from Unity Gaming Services.`}
                >
                    Not live yet &mdash; build {CURRENT_GAME_BUILD} takes its hours from Unity Gaming
                    Services, so these times are saved but do not open or shut the game.
                </div>
            ) : live.length === 0 && later.length === 0 ? (
                <div className='rounded border border-red-400/40 px-3 py-2 text-red-400'>
                    Not scheduled live &mdash; nobody can get in until a time is set.
                </div>
            ) : null}

            {live.length > 0 && (
                <div className='flex flex-col gap-2'>
                    <span className='text-[10px] uppercase tracking-widest text-green-400'>Open now</span>
                    {live.map(row)}
                </div>
            )}

            {later.length > 0 && (
                <div className='flex flex-col gap-2'>
                    <span className='text-[10px] uppercase tracking-widest text-white/40'>
                        Scheduled · {later.length} to come
                    </span>
                    {later.map(row)}
                </div>
            )}

            {/* This is how the next playtest gets put on the calendar, so it
                reads as a button rather than as a dashed afterthought. */}
            <button
                type='button'
                onClick={addWindow}
                className='self-start rounded bg-amber-500 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-black shadow-lg shadow-amber-500/20 hover:bg-amber-400'
            >
                <FontAwesomeIcon icon={faPlus} className='mr-2' />
                {value.eventsList.length === 0 ? 'Schedule a time' : 'Schedule another time'}
            </button>

            {/* Past times, folded away entirely.
              *
              * They had a banner, a clear button and a show/hide toggle --
              * three controls about times that have already happened, above
              * the ones that have not. The only thing worth saying is that
              * they are still on the banner players see, and that fits on the
              * toggle itself. */}
            {past.length > 0 && (
                <div className='flex flex-col gap-2'>
                    <span className='flex flex-row items-center gap-3'>
                        <button
                            type='button'
                            onClick={() => setShowPast((v) => !v)}
                            className='flex flex-row items-center gap-2 text-[10px] uppercase tracking-widest text-white/30 hover:text-white'
                        >
                            <FontAwesomeIcon icon={showPast ? faChevronDown : faChevronRight} />
                            {past.length} past {past.length === 1 ? 'time' : 'times'}
                        </button>
                        <span className='text-white/25'>still on the banner players see</span>
                        <button
                            type='button'
                            onClick={clearPast}
                            className='text-[10px] uppercase tracking-widest text-white/30 underline hover:text-amber-300'
                        >
                            clear
                        </button>
                    </span>
                    {showPast && past.map(row)}
                </div>
            )}


            <div className='flex flex-col gap-2'>
                <button
                    type='button'
                    onClick={() => setShowText((v) => !v)}
                    className='flex flex-row items-center gap-2 self-start text-[10px] uppercase tracking-widest text-white/40 hover:text-white'
                >
                    <FontAwesomeIcon icon={showText ? faChevronDown : faChevronRight} />
                    Other options
                </button>
                {showText && (
                    <div className='flex flex-col gap-2 border-l border-white/10 pl-3'>
                        <span className='text-[10px] text-white/30'>What players read on the closed screen.</span>
                        <label className='flex flex-col gap-1'>
                            <span className='text-[10px] uppercase tracking-widest text-white/50'>Heading</span>
                            <input
                                value={value.header}
                                onChange={(e) => set({ header: e.target.value })}
                                className={INPUT}
                            />
                        </label>
                        <label className='flex flex-col gap-1'>
                            <span className='text-[10px] uppercase tracking-widest text-white/50'>
                                Above the time
                            </span>
                            <input
                                value={value.subDescription}
                                onChange={(e) => set({ subDescription: e.target.value })}
                                className={INPUT}
                            />
                        </label>
                        <label className='flex flex-col gap-1'>
                            <span className='text-[10px] uppercase tracking-widest text-white/50'>Below it</span>
                            <input
                                value={value.footer}
                                onChange={(e) => set({ footer: e.target.value })}
                                className={INPUT}
                            />
                        </label>
                    </div>
                )}
            </div>
        </div>
    )
}
